/**
 * The automation rail — one compact row per automation (D9).
 *
 * A LIST TO CHOOSE FROM, NOT A TABLE TO READ. Until D9 each row carried a meta
 * block of four labelled fields (Kategorie, Auslöser, Status, Letzte
 * Ausführung) because the list was the main surface and the detail sat beside
 * it. In the workspace the detail is the main surface, and the rail only has to
 * answer "which one, and is it on": icon, name, status word, category and a
 * clamped description. Auslöser moved to its own tab, the run timestamp to
 * Allgemein. One thing stays in the row because it is a reason to open it: a
 * last run that FAILED says so in words.
 *
 * NO KEY, NO INVENTED STATE, NO COUNTS. The technical key addresses requests
 * and is not shown (U13). The status word is `automationStatusPresentation`,
 * the same rule the header uses, so rail and header cannot disagree.
 *
 * THERE IS STILL NO TOGGLE IN A ROW (W4). A switch inside a clickable row is a
 * mis-click waiting for a boundary; the switch lives once, in the workspace
 * header.
 *
 * THE WHOLE ROW IS THE BUTTON, AND ITS NAME IS STILL JUST THE AUTOMATION.
 * `aria-labelledby` points at the title alone; `aria-describedby` at the status
 * word and the meta block, so a screen reader hears the name first and the
 * context after. The selected row carries `aria-current="true"`, which is what
 * the eye sees as the tinted, outlined row.
 */
import React from "react";

import AutomationStatusPill from "./AutomationStatusPill";
import { AutomationIconTile, automationCategoryIcon } from "./automationIcons";
import { automationCategoryLabel } from "./automationLabels";

export default function AutomationList({ items, selectedKey, onSelect }) {
  if (!items.length) {
    return (
      <p className="m-0 rounded-[10px] border border-dashed border-border px-3 py-5 text-center text-[12.5px] text-muted-foreground">
        Keine Automatisierung gefunden.
      </p>
    );
  }

  return (
    // Named, because a bare <ul> has no accessible name at all — and because
    // "is it in the LIST" is a question the tests have to be able to ask
    // without matching the workspace's copy of the same label.
    <ul
      aria-label="Automatisierungen"
      className="m-0 flex list-none flex-col gap-1.5 p-0"
    >
      {items.map((automation) => {
        const isSelected = automation.key === selectedKey;
        const titleId = `automation-row-title-${automation.key}`;
        const statusId = `automation-row-status-${automation.key}`;
        const metaId = `automation-row-meta-${automation.key}`;
        const lastRunFailed = automation?.last_run?.status === "failed";

        return (
          <li key={automation.key}>
            <button
              type="button"
              onClick={() => onSelect(automation.key)}
              aria-current={isSelected ? "true" : undefined}
              aria-labelledby={titleId}
              aria-describedby={`${statusId} ${metaId}`}
              className={[
                "flex w-full items-start gap-3 rounded-[10px] border px-3 py-2.5 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isSelected
                  ? "border-primary/40 bg-primary/[0.07] ring-1 ring-inset ring-primary/30"
                  : "border-transparent hover:border-border hover:bg-muted/60",
              ].join(" ")}
            >
              <AutomationIconTile
                icon={automationCategoryIcon(automation.category)}
                tone="neutral"
                size="sm"
              />

              <span className="flex min-w-0 flex-1 flex-col gap-1">
                {/* PC4: wraps instead of squeezing. A long one-word title beside a
                    long status word ("Zahlungsbestätigung" / "In Vorbereitung")
                    would otherwise be broken mid-word; now the pill moves under
                    the title. The title's longest word is its minimum width. */}
                <span className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
                  <span
                    id={titleId}
                    className="grow basis-0 break-words text-[13px] font-semibold leading-snug text-foreground"
                  >
                    {automation.label}
                  </span>
                  <span id={statusId} className="shrink-0">
                    <AutomationStatusPill automation={automation} />
                  </span>
                </span>

                <span id={metaId} className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {automationCategoryLabel(automation.category)}
                  </span>
                  <span className="line-clamp-2 text-[11.5px] leading-relaxed text-muted-foreground">
                    {automation.description}
                  </span>
                  {lastRunFailed ? (
                    <span className="text-[11px] text-destructive">
                      Letzte Ausführung fehlgeschlagen
                    </span>
                  ) : null}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
